import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { BasicResponse } from 'src/app/core/interfaces/basicResponse';
import { Company, Model } from 'src/app/core/interfaces/company';
import { CostCenter } from 'src/app/core/interfaces/costCenter';
import { Permission } from 'src/app/core/resources/permission';
import { ReactiveForm } from 'src/app/core/resources/reactive-form';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CompaniesService } from '../list-companies.service';
import { Utils } from 'src/app/core/resources/utils';
import { CostCenterFormComponent } from '../cost-center-form/cost-center-form.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { AuthService } from 'src/app/core/services/authentication.service';
import { environment } from 'src/environments/environment';
import { IntegrationEnum } from 'src/app/core/enums/integration.enum';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';


@Component({
  selector: 'app-cost-center-list',
  templateUrl: './cost-center-list.component.html',
  styleUrls: ['./cost-center-list.component.scss']
})
export class CostCenterListComponent {

  permission = Permission;
  @Input() company: Company;
  @Input() costCenters: CostCenter[] = [];
  @Input() activeTab: string;
  @Output() public refreshList: EventEmitter<any> = new EventEmitter();
  private catalogGeneralCostCenter: CostCenter[] = [];
  public listGeneralCostCenter: Observable<CostCenter[]>;
  public costCenterCtrl: FormControl = new FormControl('', Validators.required);
  public transportNit: string = null;
  // El listado y el catálogo dependen de la empresa de transporte: no se piden hasta que la pastilla la resuelva.
  private transportCompanyResolved: boolean = false;
  // Centros asociados a la empresa cliente para la empresa de transporte elegida (GET company/{nit}/cost-center).
  public associatedCostCenters: CostCenter[] = [];
  public associatedLoading: boolean = false;
  // Empresa de transporte para la que ya se pidió el catálogo de Siigo (null: hay que pedirlo).
  private catalogTransportKey: string = null;

  constructor(
    private companiesService: CompaniesService,
    private spinner: NgxSpinnerService,
    private formBuilder: FormBuilder,
    private snackBarService: SnackBarService,
    private modelCompany: Model,
    public dialog: MatDialog,
    private authService: AuthService,
    public permissionRole: PermissionRole,
    public utils: Utils
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.activeTab && this.isCostCenterTabActive && this.transportCompanyResolved)
      this.refreshCostCenters();
  }

  public onTransportCompanyChange(transportNit: string): void {
    this.transportNit = transportNit;
    this.transportCompanyResolved = true;
    this.costCenterCtrl.setValue('');
    this.costCenterCtrl.markAsUntouched();
    if (this.isCostCenterTabActive) this.refreshCostCenters();
  }

  /** Listado de asociados y, si puede asociar, el catálogo del buscador. */
  private refreshCostCenters(): void {
    this.getAssociatedCostCenters();
    if (this.canAssociateCostCenter) this.getCostCenters();
  }

  public getAssociatedCostCenters(): void {
    if (!this.company || !this.company.companyId) return;
    const requestedTransportNit = this.transportNit;
    this.associatedLoading = true;
    this.companiesService.getCompanyCostCenters(this.company.companyId, requestedTransportNit).toPromise()
      .then((costCenters) => {
        // Respuesta de una empresa que ya no está seleccionada: se descarta.
        if (requestedTransportNit !== this.transportNit) return;
        this.setAssociatedCostCenters(costCenters || []);
      })
      .catch(() => {
        if (requestedTransportNit !== this.transportNit) return;
        this.setAssociatedCostCenters([]);
        this.snackBarService.openSnackBar(ServiceMessages.ERROR_GETTING_COMPANY_COST_CENTERS, undefined, 'error');
      })
      .finally(() => {
        if (requestedTransportNit === this.transportNit) this.associatedLoading = false;
      });
  }

  /**
   * Tras asociar, crear o eliminar: refresca el detalle de la empresa y el listado. Si la respuesta
   * trae la lista actualizada se usa directamente; si no, se vuelve a consultar.
   */
  public onCostCentersChanged(costCenters?: CostCenter[]): void {
    this.refreshList.emit();
    if (costCenters) this.setAssociatedCostCenters(costCenters);
    else this.getAssociatedCostCenters();
  }


  /** Actualiza los asociados y recrea el buscador para que deje de ofrecer los ya asociados. */
  private setAssociatedCostCenters(costCenters: CostCenter[]): void {
    this.associatedCostCenters = costCenters;
    this.createInstanceAutocomplete();
  }

  private isAlreadyAssociated(costCenter: CostCenter): boolean {
    const code = costCenter && costCenter.code ? costCenter.code.toString().trim().toLowerCase() : '';
    return !!code && this.associatedCostCenters.some((associated) =>
      associated && associated.code && associated.code.toString().trim().toLowerCase() === code);
  }

  private get isCostCenterTabActive(): boolean {
    return !!this.activeTab && this.activeTab === 'Centros de Costo';
  }

  /**
   * Catálogo de Siigo para la empresa de transporte elegida. Si ya se pidió para ella no se vuelve a
   * pedir al reentrar a la pestaña (force lo obliga): así el buscador no queda vacío mientras recarga.
   */
  public getCostCenters(force: boolean = false): void {
    const requestedTransportNit = this.transportNit;
    const catalogKey = requestedTransportNit || '';
    if (!force && this.catalogTransportKey === catalogKey) return;
    this.catalogTransportKey = catalogKey;
    this.spinner.show();
    // Mientras responde no deben quedar opciones de la empresa de transporte anterior.
    this.catalogGeneralCostCenter = [];
    this.createInstanceAutocomplete();
    this.companiesService.getCostCenters(requestedTransportNit).toPromise()
      .then((success) => {
        // Respuesta de una empresa que ya no está seleccionada: se descarta.
        if (requestedTransportNit !== this.transportNit) return;
        this.catalogGeneralCostCenter = Array.isArray(success) ? success : [];
        this.createInstanceAutocomplete();
      })
      .catch((error) => {
        if (requestedTransportNit !== this.transportNit) return;
        // Sin caché ante error: al reentrar a la pestaña se reintenta.
        this.catalogTransportKey = null;
        this.catalogGeneralCostCenter = [];
        this.createInstanceAutocomplete();
      })
      .finally(() => this.spinner.hide());
  }

  private createInstanceAutocomplete(): void {
    // Se recrea también con catálogo vacío: si no, el async pipe conserva las opciones de la empresa anterior.
    this.listGeneralCostCenter = this.costCenterCtrl.valueChanges.pipe(
      startWith(''),
      distinctUntilChanged(),
      map(value => {
        // Con una opción ya elegida se muestra el catálogo completo: filtrar por su nombre dejaba una
        // sola opción (o ninguna) al volver a abrir el buscador.
        const query = typeof value === 'string' ? this.normalizeText(value) : '';
        // Los ya asociados no se ofrecen: evita el llamado que el backend rechaza por duplicado.
        // Las opciones sin nombre se omiten: antes rompían el filtro y el buscador dejaba de responder.
        return this.catalogGeneralCostCenter.filter((option) => {
          return option && option.name && !this.isAlreadyAssociated(option) &&
            this.normalizeText(option.name).indexOf(query) !== -1;
        });
      })
    );
  }

  /** Sin distinguir mayúsculas, tildes ni espacios a los extremos ("administracion " encuentra "ADMINISTRACIÓN"). */
  private normalizeText(text: string): string {
    return (text || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
  }

  public displayCostCenterSelected(costCenter: CostCenter): string {
    return costCenter ? costCenter.name || '' : '';
  }

  public onSubmit(): void {
    if (!this.isCatalogOption(this.costCenterCtrl.value)) {
      this.costCenterCtrl.markAsTouched();
      this.snackBarService.openSnackBar('Debes seleccionar un Centro de Costo', undefined, 'alert');
    } else if (this.isAlreadyAssociated(this.costCenterCtrl.value)) {
      this.snackBarService.openSnackBar(ServiceMessages.COST_CENTER_ALREADY_ASSOCIATED, undefined, 'alert');
    } else {
      this.associateCostCenter(this.costCenterCtrl.value);
    }
  }

  /** Solo cuenta una opción elegida del buscador: texto libre o un centro fuera del catálogo actual no se envía. */
  private isCatalogOption(value: CostCenter | string): boolean {
    if (!value || typeof value !== 'object' || !value.code) return false;
    const code = value.code.toString().trim().toLowerCase();
    return this.catalogGeneralCostCenter.some((option) =>
      option && option.code && option.code.toString().trim().toLowerCase() === code);
  }

  private associateCostCenter(costCenter: CostCenter): void {
    this.spinner.show();
    this.companiesService.associateCostCenter(costCenter, this.company.companyId, this.transportNit).toPromise()
      .then((response) => {
        this.spinner.hide();
        if (this.companiesService.isAssociateCostCenterSuccess(response)) {
          this.snackBarService.openSnackBar('Centro de costo asociado correctamente');
          this.costCenterCtrl.setValue('');
          this.onCostCentersChanged(this.companiesService.costCentersFromResponse(response));
        } else {
          this.snackBarService.openSnackBar(this.companiesService.costCenterResponseMessage(response), undefined, 'error');
        }
      })
      .catch((error) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar('Ocurrió un error al enviar la información', undefined, 'error');
      });
  }

  openCreateCostCenter() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      nitCompany: this.company.companyId,
      nameCompany: this.company.name,
      transportCompanyId: this.transportNit
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(CostCenterFormComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.state) {
        this.onCostCentersChanged(result.costCenters || undefined);
        if (this.canAssociateCostCenter) this.getCostCenters(true);
      }
    });
  }

  get canCreateCostCenter(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.payments.module,
      this.permission.payments.createCostCenter
    ) && !this.hasSiigoIntegration;
  }

  get canAssociateCostCenter(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.payments.module,
      this.permission.payments.associateCostCenter
    ) && this.hasSiigoIntegration;
  }

  get hasSiigoIntegration(): boolean {
    return this.authService && this.authService.getCompany().integrationCredentials &&
      this.authService.getCompany().integrationCredentials.length &&
      this.authService.getCompany().integrationCredentials.some(integration => integration && integration.integrationChannel &&
        integration.integrationChannel.toLowerCase() === IntegrationEnum.SIIGO.toLowerCase() && integration.state);
  }

}
